import React from "react";
import Highcharts from "highcharts";
import useDebouncedCallback from "../../lib/hooks/useDebouncedCallback";
import usePrevious from "../../lib/hooks/usePrevious";
import { stableColor } from "../..";

const REACT_UPDATE = "react-update";
// minmax use null values (because of serialization in createGlobalState)
// Highcharts use undefined values
export interface MinMax {
  min: number | null;
  max: number | null;
}

// this code is complex and performance sensitive
const useAxisMinMax = ({
  chartRef,
  axisId,
  minMax,
  setMinMax,
}: {
  chartRef: React.RefObject<Highcharts.Chart>;
  axisId: string | null;
  minMax: MinMax;
  setMinMax: React.Dispatch<React.SetStateAction<MinMax>>;
}) => {
  const oldAxisId = usePrevious(axisId);
  // keep track of the last update done here, so that we don't loop when updating in React
  const lastUpdate = React.useRef<MinMax | null>(minMax);

  // reset it when we change axis, the highchart value is not the one we still have
  React.useLayoutEffect(
    () => () => {
      lastUpdate.current = null;
    },
    [axisId],
  );

  // update React value when user plays in Highcharts
  const handleFromToUpdate = useDebouncedCallback(
    (e: Highcharts.AxisSetExtremesEventObject) => {
      const chart = chartRef.current;
      if (!chart) {
        throw new Error("no chart yet?");
      }

      if (axisId === null) return;
      const axis = chart.axes.find(a => a.options.id === axisId);
      if (!axis) throw new Error(`Cannot find axis with id ${axisId}`);

      if (e.trigger && e.trigger !== REACT_UPDATE) {
        console.debug(
          `%c[${chart.name}] %cAXIS_CHANGED %c${axisId} %ctrigger: ${e.trigger}`,
          `color: ${stableColor(chart.name)};`,
          "color: grey;",
          `color: ${stableColor(axisId)}`,
          "color: grey;",
          axis.getExtremes(),
        );

        const newExtremes = {
          min: e.userMin ?? e.min,
          max: e.userMax ?? e.max,
        };

        setMinMax(() => {
          lastUpdate.current = newExtremes;
          return newExtremes;
        });
      }
    },
    [chartRef, axisId, setMinMax],
    300,
    { maxWait: 300 },
  );
  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("no chart yet?");
    }
    if (axisId === null) return;
    const axis = chart.axes.find(a => a.options.id === axisId);
    if (!axis) throw new Error(`Cannot find axis with id ${axisId}`);

    console.debug(
      `%c[${chart.name}] %cBIND_AXIS %c[${axisId}]`,
      `color: ${stableColor(chart.name)};`,
      "color: grey;",
      `color: ${stableColor(axisId)}`,
      axis.getExtremes(),
    );

    Highcharts.addEvent(axis, "afterSetExtremes", handleFromToUpdate);
    return () =>
      Highcharts.removeEvent(
        axis,
        "afterSetExtremes",
        handleFromToUpdate as unknown as Highcharts.EventCallbackFunction<Highcharts.Axis>,
      );
  }, [chartRef, handleFromToUpdate, axisId]);

  // update axis in Highcharts when axis changes in React
  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("no chart yet?");
    }
    if (axisId === null) return;
    const axis = chart.axes.find(a => a.options.id === axisId);
    if (!axis) throw new Error(`Cannot find axis with id ${axisId}`);

    // self-inflicted update, bail
    if (lastUpdate.current?.min === minMax.min && lastUpdate.current?.max === minMax.max) return;

    axis.setExtremes(minMax.min ?? undefined, minMax.max ?? undefined, true, false, {
      trigger: REACT_UPDATE,
    });
    lastUpdate.current = {
      min: minMax.min ?? null,
      max: minMax.max ?? null,
    };
  }, [axisId, chartRef, minMax, oldAxisId]);

  // return initial extremes. We cannot fully control Highcharts axis min max
  // because of the race condition introduced with the debouncing
  return React.useMemo(
    () => ({
      /**
       * We want to preset chart axis minMax to prevent multiple render trigger in highcharts.
       */
      initialUserMin: Number.isFinite(minMax.min) ? minMax.min : null,
      initialUserMax: Number.isFinite(minMax.max) ? minMax.max : null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
};

export default useAxisMinMax;
